/**
 * Keeps companion lines -- skein winding, class kits -- in step with the
 * product they belong to.
 *
 * Winding is charged per skein, as a separate line item, because Shopify cannot
 * price an extra onto an existing line without a cart transform. That leaves the
 * two lines free to drift: order five skeins with winding, change your mind to
 * three in the cart, and you would be charged to wind five. Or remove the yarn
 * and still pay to have it wound.
 *
 * So after every cart change each winding line is matched back to its yarn by
 * the _winding_for_handle property it was added with, and either corrected to
 * the same quantity or removed if the yarn has gone.
 *
 * Runs on the cart page and behind the drawer alike, since both dispatch
 * cart:update.
 */
// Each companion kind: the hidden property naming the product a line belongs
// to, and the pending flag its page sets while an add is mid-flight. The class
// kit rides the same rails as winding -- a priced extra as its own line, kept
// in step with the line it belongs to.
const COMPANIONS = [
  { property: '_winding_for_handle', pendingFlag: '__skeinWindingPending' },
  { property: '_kit_for_handle', pendingFlag: '__classKitPending' },
  // A garment kit's optional hook and notions (kit-builder.liquid): one of
  // each per kit, so they follow the kit's quantity like winding follows yarn.
  { property: '_addon_for_handle', pendingFlag: '__kitAddonsPending' },
];

let running = false;

/** @returns {Promise<object|null>} */
async function readCart() {
  try {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.ok ? await res.json() : null;
  } catch {
    return null;
  }
}

/**
 * @param {string} key - Line item key.
 * @param {number} quantity - 0 removes the line.
 */
async function setQuantity(key, quantity) {
  await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: key, quantity }),
  });
}

async function reconcile() {
  // A correction is itself a cart change, so without this the handler would
  // answer its own event forever.
  if (running) return;
  // An add-to-cart with a companion puts the main product in first and the
  // companion a moment later. Reconciling in that window sees a cart the
  // customer never asked for -- and previously removed the companion line as
  // an orphan the instant it arrived. Wait until every pair is complete.
  if (COMPANIONS.some((c) => window[c.pendingFlag])) return;
  running = true;

  try {
    const cart = await readCart();
    if (!cart) return;

    const isCompanion = (item) =>
      item.properties && COMPANIONS.some((c) => item.properties[c.property]);

    const companionLines = cart.items.filter(isCompanion);
    if (!companionLines.length) return;

    // How many of each main product is actually in the cart. Companion lines
    // themselves are excluded, or a kit could count as the class it serves.
    const mainQuantities = new Map();
    for (const item of cart.items) {
      if (isCompanion(item)) continue;
      mainQuantities.set(item.handle, (mainQuantities.get(item.handle) ?? 0) + item.quantity);
    }

    let changed = false;
    for (const line of companionLines) {
      const c = COMPANIONS.find((c) => line.properties[c.property]);
      const wanted = mainQuantities.get(line.properties[c.property]) ?? 0;
      if (line.quantity === wanted) continue;
      // wanted of 0 removes the line, which is what should happen when the
      // product it belongs to is taken out of the cart.
      await setQuantity(line.key, wanted);
      changed = true;
    }

    if (changed) {
      // Tell the theme to redraw, but only after the corrections have landed, or
      // it repaints the totals it was already showing.
      // Shaped like the theme's CartUpdateEvent: the drawer listener reads
      // detail.data and detail.sourceId and throws on a bare event, and the
      // header bubble shows data.itemCount.
      const after = await readCart();
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: after, sourceId: 'companion-reconcile', data: { source: 'companion-reconcile', itemCount: after?.item_count ?? 0, isIncremental: false } },
      }));
    }
  } finally {
    running = false;
  }
}

document.addEventListener('cart:update', () => {
  // Let the change that triggered this finish before reading the cart back.
  setTimeout(reconcile, 120);
});
document.addEventListener('DOMContentLoaded', reconcile);
